import { getCourseResources } from 'core/crud/courses.crud';
import { useOrganization } from 'core/hooks/useOrganization';
import useSystemSettings from 'core/hooks/useSystemSettings';
import { useUser } from 'core/lite/hooks/useUser';
import { getCourseItemsService } from 'core/services/courseItems.service';
import * as coreEntities from 'core/store/index';
import { COURSE_TYPES } from 'core/utils/constants/course';
import { getCustomizedBy, getGuidCourseProgram } from 'core/utils/course';
import { decodeHtmlWithWhiteList } from 'core/utils/strings';
import { CLIENT, CONTENT_TYPES, LESSON_TYPES_IDS } from 'core/variables/constant';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

// Referencia estable: evita recalcular los memos mientras las peticiones no han resuelto.
const NO_ITEMS = [];

/**
 * Aplana los grupos de las secciones de tipo test en opciones de select (Edelvives).
 * @param {Array} sections - Secciones de recursos del curso
 * @returns {Array} Opciones de grupo de evaluación
 */
function parseTestGroups(sections = []) {
  return sections
    .filter((section) => section.type === 'test')
    .flatMap((section) =>
      (section.items ?? []).map((group) => ({
        value: `${section.guid}|${group.guid}`,
        label: group.name || section.name,
        sectionGuid: section.guid,
        groupGuid: group.guid,
        data: { section, group },
      }))
    );
}

/**
 * Nombre del libro. Fuente única para la etiqueta y la ordenación: si divergen, la lista se
 * ordena por un criterio distinto del que muestra.
 */
function getProgramName(item) {
  return item.name || item.title;
}

/**
 * Una fila de front/courses es programa x grupo: el nombre del libro no la identifica por sí
 * solo. Mismo orden que el dropdown de cursos del header (grupo primero).
 */
function getLocationLabel(item) {
  const programName = getProgramName(item);
  return item.school_group_name ? `${item.school_group_name} · ${programName}` : programName;
}

/**
 * Convención del repo (core/utils/parses.js): `numeric` evita que «10ºA» preceda a «5ºA».
 */
function compareNames(a, b) {
  return (a ?? '').localeCompare(b ?? '', undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Custom hook for managing quiz location selection dialog state and logic
 * @param {Object} params
 * @param {boolean} params.isOpen - Whether the dialog is open
 * @param {Function} params.onClose - Closes the dialog
 * @param {Function} [params.onNavigate] - Called right before navigating to the quiz creation page
 * @returns {Object} State and handlers for the quiz location dialog
 */
function useQuizLocationDialog({ isOpen, onClose, onNavigate }) {
  const { t } = useTranslation(['chatbot', 'actions']);
  const dispatch = useDispatch();
  const history = useHistory();
  const { client, tenantConfig } = useOrganization();
  const { enableOverlays } = useSystemSettings();
  const { schoolGuid, user } = useUser();

  // Detect Edelvives tenant - uses different flow (toolbox/resources page)
  const isEdelvives = client === CLIENT.EDELVIVES || window.location.hostname.includes('edv.tenant-ai-2.oneclicklabs.es');

  // Los tenants con integraciones de terceros ocultan los programas licenciados sin clase en
  // el resto del producto. Solo aplica a `front/programs`: ocultar además las clases sin grupo
  // quitaría una ubicación que hoy funciona.
  const shouldShowPrograms = tenantConfig?.showDisregardedPrograms ?? true;

  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);

  const [units, setUnits] = useState([]);

  // Edelvives: Test sections for deep link
  const [testSections, setTestSections] = useState([]);
  const [selectedTestGroup, setSelectedTestGroup] = useState(null);
  const [hasTestSection, setHasTestSection] = useState(false);

  const [isLoadingUnits, setIsLoadingUnits] = useState(false);
  const [isLoadingTestSections, setIsLoadingTestSections] = useState(false);
  const [hasLoadedUnits, setHasLoadedUnits] = useState(false);
  const [hasLoadedTestSections, setHasLoadedTestSections] = useState(false);

  // Las clases (`front/courses`) son programa x grupo: un profesor con licencia de programa y
  // sin grupo no tiene ninguna, así que se le ofrecen también sus programas.
  const classes = useSelector((state) => coreEntities.courses.selectors.getCourses(state)) ?? NO_ITEMS;
  // El selector reconstruye su resultado en cada llamada: con la lista vacía daría un array nuevo por render.
  const programs = useSelector((state) => {
    const items = coreEntities.programs.selectors.getPrograms(state)?.programs;
    return items?.length ? items : NO_ITEMS;
  });

  useEffect(() => {
    dispatch(coreEntities.courses.actions.getCourses({ params: { offset: 0, pageSize: 1000 } }));
    dispatch(coreEntities.programs.actions.getPrograms({ isBook: 1, orderBy: 't.name + asc ' }));
  }, [dispatch]);

  // Se clasifica por si la fila tiene grupo de clase, no por el origen de los datos:
  // `front/courses` también devuelve filas sin `school_group_guid` (igual que useCourseItems).
  const { classOptions, programOptions } = useMemo(() => {
    const withGroup = [];
    const withoutGroup = [];

    classes.forEach((item) => {
      const option = {
        // Una clase sin grupo conserva su guid de curso: es el que esperan los endpoints.
        value: item.guid,
        label: getLocationLabel(item),
        data: item,
        shouldUsePreview: !item.school_group_guid,
      };

      if (option.shouldUsePreview) withoutGroup.push(option);
      else withGroup.push(option);
    });

    if (shouldShowPrograms) {
      // El guid de programa de una clase depende de su tipo: se dedupe contra los dos, y la
      // semilla incluye las clases de los dos cubos.
      const knownProgramGuids = new Set(classes.flatMap((item) => [item.program_guid, getGuidCourseProgram(item)].filter(Boolean)));

      programs.forEach((item) => {
        const programGuid = item.program?.guid ?? item.guid;
        if (!programGuid || knownProgramGuids.has(programGuid)) return;
        knownProgramGuids.add(programGuid);
        withoutGroup.push({
          value: programGuid,
          label: item.program?.name ?? item.name,
          // El resto del flujo trabaja con el guid del programa, como parsePrograms.
          data: { ...item, guid: programGuid },
          // Se abre por el guid del programa, no por el de una clase: siempre necesita preview.
          shouldUsePreview: true,
        });
      });
    }

    // Las clases, por grupo y a igual grupo por libro. El cubo sin grupo mezcla dos orígenes,
    // así que se ordena entero. Siempre sobre los arrays locales, nunca sobre el de redux.
    withGroup.sort((a, b) => compareNames(a.data.school_group_name, b.data.school_group_name) || compareNames(getProgramName(a.data), getProgramName(b.data)));
    withoutGroup.sort((a, b) => compareNames(a.label, b.label));

    return { classOptions: withGroup, programOptions: withoutGroup };
  }, [classes, programs, shouldShowPrograms]);

  // Con un solo tipo de ubicación las cabeceras de sección no aportan nada.
  const courseOptions = useMemo(() => {
    if (classOptions.length === 0 || programOptions.length === 0) return [...classOptions, ...programOptions];

    return [
      { label: t('chatbot:quiz_location_group_classes'), options: classOptions },
      { label: t('chatbot:quiz_location_group_programs'), options: programOptions },
    ];
  }, [classOptions, programOptions, t]);

  // MINT queda fuera: Edelvives usa otro flujo.
  const lessons = useMemo(() => {
    const unitLessons = selectedUnit?.data?.items ?? NO_ITEMS;
    return unitLessons.filter((lesson) => lesson.lesson_type !== LESSON_TYPES_IDS.MINT);
  }, [selectedUnit]);

  useEffect(() => {
    if (!selectedCourse || isEdelvives) return undefined;

    // enableOverlays decide el endpoint (overlays vs base): esperar a que los system settings
    // resuelvan para no cargar la versión equivocada del programa.
    if (enableOverlays === undefined) return undefined;

    // Evita escribir estado obsoleto si se cambia de ubicación antes de que resuelva
    let isCancelled = false;

    async function loadCourseUnits() {
      setIsLoadingUnits(true);
      setUnits([]);

      try {
        const course = selectedCourse.data ?? {};
        // Los items de `front/programs` no traen `users`; getCustomizedBy cae entonces en el
        // guid del usuario actual, que es justo lo que necesita el endpoint de overlays.
        const customizedBy = getCustomizedBy(course, user) ?? [];
        // Sin `preview` el backend responde 403 en las ubicaciones sin grupo y en los programas
        // licenciados, y el interceptor global de axios desloguea al profesor.
        const isDisregarded = selectedCourse.shouldUsePreview;
        const response = await getCourseItemsService(selectedCourse.value, schoolGuid, customizedBy, enableOverlays, isDisregarded);

        if (isCancelled) return;
        setUnits(Array.isArray(response) ? response : (response?.items ?? []));
      } catch (error) {
        if (isCancelled) return;
        console.error('[QuizLocationDialog] Error loading units:', error);
        setUnits([]);
      } finally {
        // Se apaga siempre: si no, al cancelar por cierre el spinner sobrevive a la reapertura.
        setIsLoadingUnits(false);
        if (!isCancelled) setHasLoadedUnits(true);
      }
    }

    loadCourseUnits();

    return () => {
      isCancelled = true;
    };
    // Se depende del guid y no de la referencia: un selector que devuelva copia relanzaría.
  }, [selectedCourse, isEdelvives, enableOverlays, schoolGuid, user?.guid]);

  // Load test sections for the Edelvives deep link
  useEffect(() => {
    if (!isEdelvives || !selectedCourse) return undefined;

    let isCancelled = false;

    async function loadTestSections() {
      setIsLoadingTestSections(true);
      setTestSections([]);
      setSelectedTestGroup(null);
      setHasTestSection(false);

      try {
        const course = selectedCourse.data ?? {};
        const courseGuid = getGuidCourseProgram(course) ?? course.guid;
        const referencedCourseGuid = course.type === COURSE_TYPES.REFERENCED ? course.guid : null;
        // Mismo motivo que en las unidades: sin `preview`, 403 y logout.
        const isDisregarded = selectedCourse.shouldUsePreview;
        const params = isDisregarded ? { preview: 1 } : undefined;

        const response = await getCourseResources({ courseGuid, referencedCourseGuid, params });
        if (isCancelled) return;

        const sections = response?.data?.data?.items ?? [];

        setHasTestSection(sections.some((section) => section.type === 'test'));

        const testGroups = parseTestGroups(sections);
        setTestSections(testGroups);

        if (testGroups.length === 1) {
          setSelectedTestGroup(testGroups[0]);
        }
      } catch (error) {
        if (isCancelled) return;
        console.error('[QuizLocationDialog] Error loading test sections:', error);
        setTestSections([]);
      } finally {
        setIsLoadingTestSections(false);
        if (!isCancelled) setHasLoadedTestSections(true);
      }
    }

    loadTestSections();

    return () => {
      isCancelled = true;
    };
  }, [isEdelvives, selectedCourse]);

  const unitOptions = useMemo(() => {
    if (!selectedCourse || units.length === 0) return NO_ITEMS;

    return units.map((unit, index) => {
      const section = unit.section;
      const sectionGuid = (typeof section === 'object' ? section?.guid : unit.guid) || unit.guid;
      const sectionName = typeof section === 'object' ? section?.name : section;

      return {
        value: sectionGuid,
        label: decodeHtmlWithWhiteList(sectionName || `${t('chatbot:Unit')} ${index + 1}`),
        data: unit,
      };
    });
  }, [selectedCourse, units, t]);

  const lessonOptions = useMemo(() => {
    if (!selectedUnit || lessons.length === 0) return NO_ITEMS;

    return lessons.map((lesson, index) => ({
      value: lesson.guid,
      label: decodeHtmlWithWhiteList(lesson.lesson_name || `${t('chatbot:Lesson')} ${index + 1}`),
      data: lesson,
    }));
  }, [selectedUnit, lessons, t]);

  const resetSelections = useCallback(() => {
    setSelectedCourse(null);
    setSelectedUnit(null);
    setSelectedLesson(null);
    setUnits([]);
    setTestSections([]);
    setSelectedTestGroup(null);
    setHasTestSection(false);
    setHasLoadedUnits(false);
    setHasLoadedTestSections(false);
  }, []);

  // react-select dispara onChange también sobre la opción ya seleccionada y con la misma
  // referencia: sin el guard se limpiaría el estado sin relanzar el efecto de carga, dejando
  // el select de unidades vacío para siempre.
  const handleCourseChange = useCallback(
    (option) => {
      if (!option || option.value === selectedCourse?.value) return;
      resetSelections();
      setSelectedCourse(option);
    },
    [resetSelections, selectedCourse]
  );

  const handleTestGroupChange = useCallback((option) => {
    setSelectedTestGroup(option);
  }, []);

  const handleUnitChange = useCallback((option) => {
    setSelectedUnit(option);
    setSelectedLesson(null);
  }, []);

  const handleLessonChange = useCallback((option) => {
    setSelectedLesson(option);
  }, []);

  useEffect(() => {
    if (!isOpen) resetSelections();
  }, [isOpen, resetSelections]);

  const navigationUrl = useMemo(() => {
    if (!selectedCourse) return null;

    const courseGuid = selectedCourse.value;
    // Sin `preview=1` las vistas piden el detalle como si hubiera grupo de clase → 403.
    const shouldUsePreview = Boolean(selectedCourse.shouldUsePreview);

    function buildUrl(path, queryParams = []) {
      const params = shouldUsePreview ? [...queryParams, 'preview=1'] : queryParams;
      return params.length > 0 ? `${path}?${params.join('&')}` : path;
    }

    if (isEdelvives) {
      if (!selectedTestGroup) return null;
      return buildUrl(`/course/${courseGuid}/create-evaluations/ia/${selectedTestGroup.sectionGuid}/${selectedTestGroup.groupGuid}`);
    }

    if (!selectedUnit) return null;

    if (lessons.length === 0) {
      // El `value` es el guid del item de curso; para navegar hace falta el de la lección.
      const unitLessonGuid = selectedUnit.data?.lesson_guid || selectedUnit.value;
      return buildUrl(`/course/${courseGuid}/lesson/${unitLessonGuid}/add/${CONTENT_TYPES.TEST}`, ['enableAI=true']);
    }

    if (!selectedLesson) return null;

    const lessonGuid = selectedLesson.data?.lesson_guid || selectedLesson.value;
    const lessonType = selectedLesson.data?.lesson_type;
    const isPowerCourse = selectedCourse.data?.has_power_lesson === 1;

    if (lessonType === LESSON_TYPES_IDS.POWER || isPowerCourse) {
      return buildUrl(`/course/${courseGuid}/lesson/${lessonGuid}`, ['addQuiz=true', 'enableAI=true']);
    }

    return buildUrl(`/course/${courseGuid}/lesson/${lessonGuid}/add/${CONTENT_TYPES.TEST}`, ['enableAI=true']);
  }, [selectedCourse, selectedUnit, selectedLesson, selectedTestGroup, isEdelvives, lessons]);

  const canNavigate = useMemo(() => {
    if (isEdelvives) {
      if (!selectedCourse) return false;
      if (isLoadingTestSections) return false;
      if (testSections.length === 0) return false;
      if (testSections.length > 1) return Boolean(selectedTestGroup);
      return true;
    }

    if (lessons.length === 0) {
      return Boolean(selectedCourse && selectedUnit);
    }

    return Boolean(selectedCourse && selectedLesson);
  }, [isEdelvives, selectedCourse, selectedUnit, selectedLesson, lessons, testSections, selectedTestGroup, isLoadingTestSections]);

  const handleConfirm = useCallback(() => {
    if (navigationUrl) {
      history.push(navigationUrl);
    }
    if (onNavigate) onNavigate();
    onClose();
  }, [history, navigationUrl, onClose, onNavigate]);

  const hasLessons = lessons.length > 0;
  const hasUnitsError = hasLoadedUnits && !isLoadingUnits && unitOptions.length === 0;
  // Solo si la unidad tiene lecciones: los programas de solo módulos no son un error.
  const hasLessonsError = Boolean(selectedUnit) && !isLoadingUnits && lessonOptions.length === 0 && hasLessons;
  const hasContentError = !isEdelvives && (hasUnitsError || hasLessonsError);
  const isUnitSelectDisabled = unitOptions.length === 0 && !isLoadingUnits;
  const isLessonSelectDisabled = lessonOptions.length === 0;

  // Distingue "no hay sección de test" de "la hay, pero sin grupos".
  const hasTestSectionsLoaded = isEdelvives && hasLoadedTestSections && !isLoadingTestSections;
  const hasNoTestSection = hasTestSectionsLoaded && !hasTestSection;
  const hasNoGroups = hasTestSectionsLoaded && hasTestSection && testSections.length === 0;
  const hasMultipleTestSections = isEdelvives && testSections.length > 1;

  // SelectCustomizable pinta el contenedor accesible del error: solo espera el texto.
  const unitError = hasUnitsError ? t('chatbot:teacher_tools_error_content_unavailable_title') : undefined;
  const lessonError = hasLessonsError ? t('chatbot:teacher_tools_error_content_unavailable_title') : undefined;

  return {
    // Options
    courseOptions,
    unitOptions,
    lessonOptions,
    testSections,

    // Selected values
    selectedCourse,
    selectedUnit,
    selectedLesson,
    selectedTestGroup,

    // Loading states
    isLoadingUnits,
    isLoadingTestSections,

    // Handlers
    handleCourseChange,
    handleUnitChange,
    handleLessonChange,
    handleTestGroupChange,
    handleConfirm,

    // Computed
    canNavigate,
    hasContentError,
    hasLessons,
    hasLessonsError,
    hasMultipleTestSections,
    hasNoGroups,
    hasNoTestSection,
    hasUnitsError,
    isLessonSelectDisabled,
    isUnitSelectDisabled,
    lessonError,
    unitError,

    // Expuestos solo para los tests: el componente no los consume.
    navigationUrl,
    resetSelections,

    // Tenant flags
    isEdelvives,

    t,
  };
}

export default useQuizLocationDialog;
